import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Star, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { AssessmentData, AssessmentField } from './types';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '@/hooks/useAnalytics';
import { getCurrentUser, getAssessmentFields, submitAssessmentResponses } from '@/lib/api';

type FormFieldValue = string | number | boolean | string[];
type FormResponses = Record<string, FormFieldValue>;

const isString = (value: FormFieldValue): value is string => typeof value === 'string';
const isStringArray = (value: FormFieldValue): value is string[] => Array.isArray(value);

interface AssessmentContentProps {
  assessment: AssessmentData;
  onComplete?: () => void;
  onClose: () => void;
}

export function AssessmentContent({ assessment, onComplete, onClose }: AssessmentContentProps) {
  const [responses, setResponses] = useState<FormResponses>({});
  const { t } = useTranslation();
  const { trackFeatureUsage } = useAnalytics();

  const { data: user } = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const result = await getCurrentUser();
      return result.success ? result.data : null;
    },
  });

  const { data: fields, isLoading: fieldsLoading } = useQuery({
    queryKey: ['assessment-fields', assessment.id],
    queryFn: async () => {
      const result = await getAssessmentFields(assessment.id);
      if (!result.success) throw new Error('error' in result ? result.error.message : t('common.assessmentcontent.failed_load_fields', 'Failed to load fields'));
      return result.data as AssessmentField[];
    },
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('Not authenticated');

      const responsesToInsert = Object.entries(responses).map(([fieldId, response]) => ({
        assessment_id: assessment.id,
        user_id: user.id,
        field_id: fieldId,
        response,
      }));

      const result = await submitAssessmentResponses(responsesToInsert);
      if (!result.success) throw new Error('error' in result ? result.error.message : t('common.assessmentcontent.failed_submit', 'Failed to submit'));
    },
    onSuccess: () => {
      trackFeatureUsage('Assessment Completed', {
        assessment_id: assessment.id,
        assessment_title: assessment.title,
        fields_answered: Object.keys(responses).length,
      });
      toast.success(t('common:assessment.submitSuccess', 'Assessment submitted successfully!'));
      onComplete?.();
      onClose();
    },
    onError: () => {
      toast.error(t('common:assessment.submitError', 'Failed to submit assessment'));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const requiredFields = fields?.filter(f => f.required && f.field_type !== 'label') || [];
    const missingFields = requiredFields.filter(f => !responses[f.id]);

    if (missingFields.length > 0) {
      toast.error(t('common:assessment.requiredFields', 'Please fill in all required fields'));
      return;
    }

    submitMutation.mutate();
  };

  if (fieldsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {fields?.map((field) => {
        if (field.field_type === 'label') {
          return (
            <div key={field.id} className="pt-4">
              <h3 className="text-lg font-semibold">{field.label}</h3>
            </div>
          );
        }

        return (
          <div key={field.id}>
            <Label htmlFor={field.id}>
              {field.label}
              {field.required && <span className="text-destructive ml-1">*</span>}
            </Label>

            {field.field_type === 'text' && (
              <Input
                id={field.id}
                value={isString(responses[field.id]) ? responses[field.id] as string : ''}
                onChange={(e) =>
                  setResponses({ ...responses, [field.id]: e.target.value })
                }
                required={field.required}
                className="mt-2"
              />
            )}

            {field.field_type === 'multiline' && (
              <Textarea
                id={field.id}
                value={isString(responses[field.id]) ? responses[field.id] as string : ''}
                onChange={(e) =>
                  setResponses({ ...responses, [field.id]: e.target.value })
                }
                required={field.required}
                className="mt-2"
              />
            )}

            {field.field_type === 'number' && (
              <Input
                id={field.id}
                type="number"
                value={isString(responses[field.id]) ? responses[field.id] as string : ''}
                onChange={(e) =>
                  setResponses({ ...responses, [field.id]: e.target.value })
                }
                required={field.required}
                className="mt-2"
              />
            )}

            {field.field_type === 'single_choice' && (
              <RadioGroup
                value={isString(responses[field.id]) ? responses[field.id] as string : ''}
                onValueChange={(value) =>
                  setResponses({ ...responses, [field.id]: value })
                }
                className="mt-2"
              >
                {(field.options as string[])?.map((option, i) => (
                  <div key={i} className="flex items-center space-x-2">
                    <RadioGroupItem value={option} id={`${field.id}-${i}`} />
                    <Label htmlFor={`${field.id}-${i}`}>{option}</Label>
                  </div>
                ))}
              </RadioGroup>
            )}

            {field.field_type === 'multiple_choice' && (
              <div className="mt-2 space-y-2">
                {(field.options as string[])?.map((option, i) => (
                  <div key={i} className="flex items-center space-x-2">
                    <Checkbox
                      id={`${field.id}-${i}`}
                      checked={isStringArray(responses[field.id]) 
                        ? (responses[field.id] as string[]).includes(option) 
                        : false}
                      onCheckedChange={(checked) => {
                        const current = isStringArray(responses[field.id]) 
                          ? [...(responses[field.id] as string[])] 
                          : [];
                        const updated = checked
                          ? [...current, option]
                          : current.filter((o: string) => o !== option);
                        setResponses({ ...responses, [field.id]: updated });
                      }}
                    />
                    <Label htmlFor={`${field.id}-${i}`}>{option}</Label>
                  </div>
                ))}
              </div>
            )}

            {field.field_type === 'dropdown' && (
              <Select
                value={isString(responses[field.id]) ? responses[field.id] as string : ''}
                onValueChange={(value) =>
                  setResponses({ ...responses, [field.id]: value })
                }
              >
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder={t('common:assessment.selectOption', 'Select an option')} />
                </SelectTrigger>
                <SelectContent>
                  {(field.options as string[])?.map((option, i) => (
                    <SelectItem key={i} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {field.field_type === 'star_scale' && (
              <div className="flex gap-2 mt-2">
                {Array.from({ length: field.max_value || 5 }, (_, i) => i + 1).map(
                  (value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() =>
                        setResponses({ ...responses, [field.id]: value })
                      }
                      className="focus:outline-none"
                    >
                      <Star
                        className={`h-8 w-8 ${
                          value <= (typeof responses[field.id] === 'number' ? responses[field.id] as number : 0)
                            ? 'fill-primary text-primary'
                            : 'text-muted-foreground'
                        }`}
                      />
                    </button>
                  )
                )}
              </div>
            )}

            {field.field_type === 'slider' && (
              <div className="mt-4">
                <Slider
                  value={[typeof responses[field.id] === 'number' ? responses[field.id] as number : (field.min_value || 1)]}
                  onValueChange={([value]) =>
                    setResponses({ ...responses, [field.id]: value })
                  }
                  min={field.min_value || 1}
                  max={field.max_value || 10}
                  step={1}
                  className="w-full"
                />
                <div className="flex justify-between text-sm text-muted-foreground mt-2">
                  <span>{field.min_value || 1}</span>
                  <span className="font-medium">
                    {typeof responses[field.id] === 'number' ? responses[field.id] : (field.min_value || 1)}
                  </span>
                  <span>{field.max_value || 10}</span>
                </div>
              </div>
            )}
          </div>
        );
      })}

      <Button 
        type="submit" 
        disabled={submitMutation.isPending} 
        size="lg"
        className="w-full"
      >
        {submitMutation.isPending ? t('common:assessment.submitting', 'Submitting...') : t('common:assessment.submit', 'Submit Assessment')}
      </Button>
    </form>
  );
}
